import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError, formatCurrency, formatDateTime } from "@/lib/api";
import { log } from "@/lib/log";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { Plus, FileText, ArrowRight, Loader2, Download } from "lucide-react";

function buildCsv(rows) {
  const headers = [
    "Payee", "Amount", "Currency", "Category", "Status", "Submitted by", "Submitted at",
    "Approvers", "Approved", "Rejected", "Pending", "Finance email", "Description",
  ];
  const esc = (v) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [headers.join(",")];
  for (const e of rows) {
    const approvers = e.approvers || [];
    const approved = approvers.filter((a) => a.decision === "approved").length;
    const rejected = approvers.filter((a) => a.decision === "rejected").length;
    const pending = approvers.filter((a) => a.decision === "pending").length;
    lines.push([
      e.payee, e.amount, e.currency, e.category || "", e.status, e.created_by, e.created_at,
      approvers.length, approved, rejected, pending, e.finance_email || "", e.description,
    ].map(esc).join(","));
  }
  return lines.join("\n");
}

export default function Dashboard() {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [query, setQuery] = useState("");

  const fetchExpenses = useCallback(async () => {
    try {
      const { data } = await api.get("/expenses");
      setExpenses(data);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchExpenses(); }, [fetchExpenses]);

  const stats = {
    total: expenses.length,
    awaiting: expenses.filter((e) => e.status === "awaiting_approval").length,
    approved: expenses.filter((e) => e.status === "approved").length,
    rejected: expenses.filter((e) => e.status === "rejected").length,
  };

  const visible = expenses.filter((e) => {
    if (statusFilter !== "all" && e.status !== statusFilter) return false;
    if (query) {
      const q = query.toLowerCase();
      if (!e.payee.toLowerCase().includes(q) && !(e.description || "").toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const exportCsv = () => {
    const csv = buildCsv(visible);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 10);
    const suffix = statusFilter === "all" ? "all" : statusFilter;
    a.href = url;
    a.download = `expenses-${suffix}-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Layout>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8 animate-fade-up">
        <div>
          <p className="text-[11px] uppercase tracking-[0.18em] text-ink-soft mb-2">Dashboard</p>
          <h1 className="font-heading text-3xl sm:text-4xl text-forest-900 tracking-tight">Expenses</h1>
          <p className="text-sm text-ink-soft mt-2 max-w-lg">
            All submitted expenses awaiting sign-off. Click an expense to view its receipts, approvers, and tamper-proof audit log.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            variant="outline"
            onClick={exportCsv}
            disabled={visible.length === 0}
            data-testid="export-csv-button"
            className="flex-1 sm:flex-none h-10 border-cream-200 text-forest-800 hover:bg-cream-100"
          >
            <Download className="h-4 w-4 mr-1.5" /> Export CSV
          </Button>
          <Button
            asChild
            className="flex-1 sm:flex-none bg-forest-700 hover:bg-forest-800 text-cream-50 h-10"
            data-testid="new-expense-button"
          >
            <Link to="/expenses/new"><Plus className="h-4 w-4 mr-1.5" /> New expense</Link>
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8" data-testid="dashboard-stats">
        <StatCard label="Total" value={stats.total} testid="stat-total" />
        <StatCard label="Awaiting approval" value={stats.awaiting} testid="stat-awaiting" accent="amber" />
        <StatCard label="Approved" value={stats.approved} testid="stat-approved" accent="emerald" />
        <StatCard label="Rejected" value={stats.rejected} testid="stat-rejected" accent="rose" />
      </div>

      {/* List */}
      <div className="bg-white border border-cream-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-12 flex items-center justify-center text-ink-soft">
            <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading expenses…
          </div>
        ) : error ? (
          <div className="p-8 text-rose-800">{error}</div>
        ) : expenses.length === 0 ? (
          <EmptyState />
        ) : (
          <>
          <div className="px-5 py-3 border-b border-cream-200 flex flex-wrap gap-2 items-center bg-cream-50">
            <input
              data-testid="dashboard-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search payee or description…"
              className="flex-1 min-w-[180px] bg-white border border-cream-200 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:border-forest-700"
            />
            {["all","awaiting_approval","approved","rejected"].map((s) => (
              <button
                key={s}
                type="button"
                data-testid={`filter-${s}`}
                onClick={() => setStatusFilter(s)}
                className={`text-[11px] uppercase tracking-wider px-2.5 py-1 rounded-md border ${statusFilter===s ? "bg-forest-700 text-cream-50 border-forest-700" : "bg-white text-ink-soft border-cream-200 hover:text-forest-800"}`}
              >
                {s === "all" ? "All" : s.replace("_"," ")}
              </button>
            ))}
          </div>
          <ul className="divide-y divide-cream-200" data-testid="expenses-list">
            {visible.map((exp) => {
              const approved = exp.approvers.filter((a) => a.decision === "approved").length;
              const rejected = exp.approvers.filter((a) => a.decision === "rejected").length;
              return (
                <li key={exp.id}>
                  <Link
                    to={`/expenses/${exp.id}`}
                    data-testid={`expense-row-${exp.id}`}
                    className="block px-5 py-4 md:px-6 md:py-5 hover:bg-cream-100 transition-colors group"
                  >
                    <div className="flex items-start gap-3 sm:gap-4">
                      <ReceiptThumb exp={exp} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-2 sm:gap-3 flex-wrap">
                          <div className="font-heading text-forest-900 text-base font-semibold truncate">{exp.payee}</div>
                          <StatusBadge status={exp.status} />
                        </div>
                        <div className="text-sm text-ink-soft truncate mt-0.5">{exp.description}</div>
                        {/* Mobile-only amount + progress */}
                        <div className="sm:hidden mt-2 flex items-center justify-between gap-2">
                          <span className="font-heading text-base text-forest-800 font-semibold">
                            {formatCurrency(exp.amount, exp.currency)}
                          </span>
                          <span className="text-[11px] text-ink-mute">
                            {approved}/{exp.approvers.length} approved{rejected ? ` · ${rejected} rejected` : ""}
                          </span>
                        </div>
                        <div className="text-[11px] text-ink-mute mt-1.5 uppercase tracking-wider truncate">
                          Submitted {formatDateTime(exp.created_at)} · {exp.created_by}
                        </div>
                      </div>
                      <div className="text-right shrink-0 hidden sm:block">
                        <div className="font-heading text-lg text-forest-800 font-semibold">
                          {formatCurrency(exp.amount, exp.currency)}
                        </div>
                        <div className="text-[11px] text-ink-mute mt-1">
                          {approved}/{exp.approvers.length} approved{rejected ? ` · ${rejected} rejected` : ""}
                        </div>
                      </div>
                      <ArrowRight className="hidden sm:block h-4 w-4 text-ink-mute group-hover:text-forest-700 group-hover:translate-x-0.5 transition-all" />
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
          </>
        )}
      </div>
    </Layout>
  );
}

function ReceiptThumb({ exp }) {
  const receipts = exp.receipts && exp.receipts.length ? exp.receipts : null;
  const contentType = receipts ? receipts[0]?.content_type : exp.receipt_content_type;
  const isImage = (contentType || "").startsWith("image/");
  const count = receipts ? receipts.length : (exp.receipt_stored || exp.receipt_storage_path ? 1 : 0);
  const [thumbUrl, setThumbUrl] = useState(null);

  useEffect(() => {
    if (!isImage) return;
    let revoke;
    let active = true;
    (async () => {
      try {
        const res = await api.get(`/expenses/${exp.id}/receipt`, { responseType: "blob" });
        if (!active) return;
        const url = URL.createObjectURL(res.data);
        setThumbUrl(url);
        revoke = () => URL.revokeObjectURL(url);
      } catch (err) {
        log.debug("Receipt thumbnail unavailable, using icon fallback:", err?.message);
      }
    })();
    return () => { active = false; if (revoke) revoke(); };
  }, [exp.id, isImage]);

  return (
    <div
      className="relative h-10 w-10 rounded-md bg-forest-50 grid place-items-center shrink-0 overflow-hidden border border-cream-200"
      data-testid={`receipt-thumb-${exp.id}`}
      onClick={(e) => e.preventDefault()}
    >
      {thumbUrl ? (
        <img src={thumbUrl} alt="Receipt" className="h-full w-full object-cover" />
      ) : (
        <FileText className="h-4 w-4 text-forest-700" />
      )}
      {count > 1 && (
        <span
          className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-forest-700 text-cream-50 text-[9px] font-semibold grid place-items-center leading-none"
          data-testid={`receipt-count-${exp.id}`}
        >
          {count}
        </span>
      )}
    </div>
  );
}

function StatCard({ label, value, testid, accent }) {
  const ring = {
    amber: "before:bg-amber-400",
    emerald: "before:bg-emerald-500",
    rose: "before:bg-rose-400",
  }[accent] || "before:bg-forest-700";
  return (
    <div
      data-testid={testid}
      className={`relative bg-white border border-cream-200 rounded-xl p-4 overflow-hidden before:absolute before:left-0 before:top-0 before:bottom-0 before:w-[3px] ${ring}`}
    >
      <div className="text-[10px] uppercase tracking-[0.16em] text-ink-soft font-semibold">{label}</div>
      <div className="font-heading text-3xl text-forest-900 mt-1.5">{value}</div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="p-12 text-center" data-testid="empty-state">
      <div className="mx-auto h-12 w-12 rounded-full bg-forest-50 grid place-items-center mb-4">
        <FileText className="h-5 w-5 text-forest-700" />
      </div>
      <h3 className="font-heading text-lg text-forest-900 mb-1">No expenses yet</h3>
      <p className="text-sm text-ink-soft mb-6">
        Submit your first expense — upload your receipts and nominate 2–6 approvers.
      </p>
      <Button asChild className="bg-forest-700 hover:bg-forest-800 text-cream-50" data-testid="empty-new-expense-button">
        <Link to="/expenses/new"><Plus className="h-4 w-4 mr-1.5" /> New expense</Link>
      </Button>
    </div>
  );
}
